import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { DEFAULT_LOCALE, LOCALE_DIR, type Locale } from "../i18n/dictionary";

export interface ComfortState {
  calm: boolean;
  bigText: boolean;
  spacing: boolean;
  contrast: boolean;
  dark: boolean;
  locale: Locale;
}

interface ComfortCtx extends ComfortState {
  toggle: (key: keyof Omit<ComfortState, "locale">) => void;
  setLocale: (locale: Locale) => void;
}

const DEFAULTS: ComfortState = {
  calm: false,
  bigText: false,
  spacing: false,
  contrast: false,
  dark: false,
  locale: DEFAULT_LOCALE,
};

const STORAGE_KEY = "zp-comfort";

function loadFromStorage(): ComfortState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<ComfortState>) };
    }
  } catch {
    // ignore
  }
  return DEFAULTS;
}

export const ComfortContext = createContext<ComfortCtx>({
  ...DEFAULTS,
  toggle: () => undefined,
  setLocale: () => undefined,
});

export function ComfortProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ComfortState>(loadFromStorage);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore
    }
  }, [state]);

  // Apply comfort data-attributes to <html> so CSS selectors work globally.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-z-theme", state.dark ? "dark" : "light");
    root.setAttribute("data-z-calm", String(state.calm));
    root.setAttribute("data-z-bigtext", String(state.bigText));
    root.setAttribute("data-z-spacing", String(state.spacing));
    root.setAttribute("data-z-contrast", String(state.contrast));
    root.setAttribute("dir", LOCALE_DIR[state.locale]);
    root.setAttribute("lang", state.locale);
  }, [state]);

  const toggle = useCallback(
    (key: keyof Omit<ComfortState, "locale">) => {
      setState((s) => ({ ...s, [key]: !s[key] }));
    },
    [],
  );

  const setLocale = useCallback((locale: Locale) => {
    setState((s) => ({ ...s, locale }));
  }, []);

  return (
    <ComfortContext.Provider value={{ ...state, toggle, setLocale }}>
      {children}
    </ComfortContext.Provider>
  );
}

export function useComfort(): ComfortCtx {
  return useContext(ComfortContext);
}
